import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useToast } from '@/shared/providers';
import { feriadosService } from '../services/feriadosService';
import { type AbrangenciaFeriado, type CalendarioAno, type CalendarioResumo, type FeriadoInput } from '../types/feriadosTypes';
type CarregamentoStatus = 'loading' | 'succeeded' | 'failed';
export type FiltroAbrangencia = 'todas' | AbrangenciaFeriado;
const FERIADOS_BASE_PATH = '/admin-geral/secretaria/feriados';
export function useCalendarioDetalhe(ano: number) {
  const router = useRouter();
  const toast = useToast();
  const [calendario, setCalendario] = useState<CalendarioAno | null>(null);
  const [status, setStatus] = useState<CarregamentoStatus>('loading');
  const [salvando, setSalvando] = useState(false);
  const [filtroAbrangencia, setFiltroAbrangencia] = useState<FiltroAbrangencia>('todas');
  const [anosDisponiveis, setAnosDisponiveis] = useState<CalendarioResumo[]>([]);
  const recarregar = useCallback(() => {
    setStatus('loading');
    Promise.all([feriadosService.obterCalendario(ano), feriadosService.listarCalendarios()]).then(([detalhe, lista]) => {
      setCalendario(detalhe);
      setAnosDisponiveis(lista.filter(item => item.ano !== ano));
      setStatus('succeeded');
    }).catch(() => setStatus('failed'));
  }, [ano]);
  useEffect(() => {
    recarregar();
  }, [recarregar]);
  const feriadosFiltrados = useMemo(() => {
    if (!calendario) return [];
    if (filtroAbrangencia === 'todas') return calendario.feriados;
    return calendario.feriados.filter(feriado => feriado.abrangencia === filtroAbrangencia);
  }, [calendario, filtroAbrangencia]);
  const executar = async (acao: () => Promise<CalendarioAno>, sucesso: string): Promise<boolean> => {
    setSalvando(true);
    try {
      const atualizado = await acao();
      setCalendario(atualizado);
      toast.showSuccess(sucesso);
      return true;
    } catch (error) {
      toast.showError('Não foi possível concluir', error instanceof Error ? error.message : undefined);
      return false;
    } finally {
      setSalvando(false);
    }
  };
  const adicionarFeriado = (input: FeriadoInput) => executar(() => feriadosService.adicionarFeriado(ano, input), 'Feriado adicionado');
  const editarFeriado = (id: string, input_0: FeriadoInput) => executar(() => feriadosService.editarFeriado(ano, id, input_0), 'Feriado atualizado');
  const removerFeriado = (id_0: string) => executar(() => feriadosService.removerFeriado(ano, id_0), 'Feriado removido');
  const importarNacionais = async (datas: string[]): Promise<boolean> => {
    setSalvando(true);
    try {
      const resultado = await feriadosService.importarNacionais(ano, datas);
      setCalendario(resultado.calendario);
      toast.showSuccess('Feriados importados', `${resultado.importados} importado(s)${resultado.ignorados > 0 ? `, ${resultado.ignorados} já existia(m)` : ''}.`);
      return true;
    } catch (error_0) {
      toast.showError('Não foi possível importar', error_0 instanceof Error ? error_0.message : undefined);
      return false;
    } finally {
      setSalvando(false);
    }
  };
  const copiarDeAno = async (anoOrigem: number): Promise<boolean> => {
    setSalvando(true);
    try {
      const resultado_0 = await feriadosService.copiarFeriados(ano, anoOrigem);
      setCalendario(resultado_0.calendario);
      toast.showSuccess('Feriados copiados', `${resultado_0.copiados} copiado(s)${resultado_0.ignorados > 0 ? `, ${resultado_0.ignorados} já existia(m)` : ''}.`);
      return true;
    } catch (error_1) {
      toast.showError('Não foi possível copiar', error_1 instanceof Error ? error_1.message : undefined);
      return false;
    } finally {
      setSalvando(false);
    }
  };
  const voltar = () => router.push(FERIADOS_BASE_PATH);
  return {
    calendario,
    status,
    salvando,
    filtroAbrangencia,
    setFiltroAbrangencia,
    feriadosFiltrados,
    anosDisponiveis,
    adicionarFeriado,
    editarFeriado,
    removerFeriado,
    importarNacionais,
    copiarDeAno,
    voltar
  };
}